import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiResponseCode } from 'projects/bo/src/app/shared/constants/api-response-code';
import { BehaviorSubject, catchError, filter, finalize, map, Observable, switchMap, take, throwError } from 'rxjs';
import { AccountApiService } from '../../api/account/account-api.service';
import { TokenState } from '../../states/token.service';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root'
})
export class RefreshTokenService {
  private readonly authService = inject(AuthService);
  private readonly accountApiService = inject(AccountApiService);
  private readonly tokenState = inject(TokenState);
  private refreshTokenInProgress: boolean = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  public refreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.updateRefreshToken()?.subscribe();

    // Waiting for refreshToken to finish updating...
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(() => {
        if (!req || !next) {
          return new Observable<HttpEvent<any>>();
        } else {
          return next.handle(this.setToken(req));
        }
      }),
      finalize(() => {
        this.refreshTokenInProgress = false;
      })
    );
  }

  private updateRefreshToken(): Observable<any> | undefined {
    const oldToken = this.tokenState.get();
    if (!oldToken || this.refreshTokenInProgress) {
      return;
    }
    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    return this.accountApiService
      .RefreshUserToken({
        token: oldToken.token,
        refreshToken: oldToken.refreshToken
      })
      .pipe(
        take(1),
        map(res => {
          if (res.code == ApiResponseCode.Success) {
            this.tokenState.setState(res.data);
            this.refreshTokenSubject.next(res.data.token);
          } else {
            const errorCode = Number(res.code.toString().substring(4));
            if (errorCode == ApiResponseCode.SessionExpired) {
              this.authService.logout();
            } else {
              this.refreshTokenSubject.next(oldToken.token);
            }
          }
          return;
        }),
        catchError(error => {
          return throwError(error);
        }),
        finalize(() => {
          this.refreshTokenInProgress = false;
        })
      );
  }

  private setToken(req: HttpRequest<any>): HttpRequest<any> {
    const accessToken = this.tokenState.getTokenOrEmpty();
    if (accessToken) {
      return req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
      });
    } else {
      return req;
    }
  }
}
